import * as S from './Badge.styles';
import Dot from '../dot/Dot';
import Span from '../texts/span/Span';
import Icon from '../icon/Icon';

const defaultProps = {
  type: 'text',
  variant: 'neutral-1',
  size: 'md',
  withOval: false,
  border: null,
  weight: 'regular',
};

type BadgeProps = {
  type: 'dot' | 'text' | 'notification';
  variant:
    | string
    | 'danger'
    | 'info'
    | 'warning'
    | 'success'
    | 'neutral'
    | 'neutral-strong'
    | 'primary';
  size: 'sm' | 'md';
  border?: 'primary' | 'success' | 'info' | 'error' | 'none';
  iconName?: string;
  weight?: 'regular' | 'bold';
  children: any;
};

const Badge = (props: BadgeProps) => {
  const { children, type, variant, iconName, size, border, weight } = props;

  const innerProps: Record<string, any> = {
    danger: {
      bg: {
        variant: 'danger',
        modifier: 'regular',
      },
      text: {
        variant: 'danger',
        modifier: 'strong',
      },
      ic: {
        variant: 'danger',
        modifier: 'regular',
      },
    },
    info: {
      bg: {
        variant: 'info',
        modifier: 'regular',
      },
      text: {
        variant: 'info',
        modifier: 'regular',
      },
      ic: {
        variant: 'info',
        modifier: 'regular',
      },
    },
    warning: {
      bg: {
        variant: 'warning',
        modifier: 'regular',
      },
      text: {
        variant: 'warning',
        modifier: 'regular',
      },
      ic: {
        variant: 'warning',
        modifier: 'regular',
      },
    },
    success: {
      bg: {
        variant: 'success',
        modifier: 'stronger',
      },
      text: {
        variant: 'success',
        modifier: 'regular',
      },
      ic: {
        variant: 'success',
        modifier: 'regular',
      },
    },
    neutral: {
      bg: {
        variant: 'neutral',
        modifier: 'stronger',
      },
      text: {
        variant: 'neutral',
        modifier: 'inverted',
      },
      ic: {
        variant: 'neutral',
        modifier: 'inverted',
      },
    },
    'neutral-strong': {
      bg: {
        variant: 'neutral',
        modifier: 'muted-strong',
      },
      text: {
        variant: 'neutral',
        modifier: 'inverted',
      },
      ic: {
        variant: 'neutral',
        modifier: 'inverted',
      },
    },
  };

  const safeVariant = innerProps[variant] ? variant : 'info';

  const renderDotBadge = () => (
    <>
      <Dot
        variant={innerProps[safeVariant].ic.variant}
        modifier={innerProps[safeVariant].ic.modifier}
      />
      <Span type='5' variant='neutral-1' modifier='strong'>
        {children}
      </Span>
    </>
  );

  const renderNotificationBadge = () => (
    <Span type='5' variant='neutral-1' modifier='inverted'>
      {children}
    </Span>
  );

  const renderTextBadge = () => (
    <>
      {iconName && (
        <Icon
          name={iconName}
          variant={innerProps[safeVariant].ic.variant}
          modifier={innerProps[safeVariant].ic.modifier}
          height={16}
          width={16}
        />
      )}
      <Span
        type='5'
        variant={innerProps[safeVariant].text.variant}
        modifier={innerProps[safeVariant].text.modifier}
        weight={weight}
      >
        {children}
      </Span>
    </>
  );

  const badgeToRender = {
    dot: () => renderDotBadge(),
    notification: () => renderNotificationBadge(),
    text: () => renderTextBadge(),
  };

  return (
    <S.Badge
      variant={innerProps[safeVariant].bg.variant}
      type={type}
      modifier={innerProps[safeVariant].bg.modifier}
      size={size}
      border={border}
    >
      {<>{badgeToRender[type]()}</>}
    </S.Badge>
  );
};

Badge.defaultProps = defaultProps;

export default Badge;
